import { useEffect, useRef } from 'react';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ClientLogRequest, Feature } from 'src/WebApiController';

import { useAppContext } from '../AppContext';
import { POSSignalRHub } from '../SignalRConnection';

// Lightweight console-based logger — this context lives above RootErrorBoundary
// where ClientLogContextProvider is not available.
const hubLogger = (req: ClientLogRequest) =>
  console.warn('[AdminHub]', req.error, req.additionalInfo ?? '');

/**
 * Dedicated SignalR context for server-initiated admin operations (e.g. force logout).
 * Connects to /hubs/admin (Azure SignalR hub name: "adminhub").
 *
 * Gated behind the AdminHubSignalR (100540) feature flag.
 *
 * Mounted ABOVE RootErrorBoundary and ThemeProvider so it survives app
 * crashes — the primary scenario it needs to handle (POS-8568).
 */
export function AdminHubContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { activeAccountWebClientConfig } = useAppContext();
  const hubRef = useRef<POSSignalRHub>();

  const isEnabled = useUserHasFeature(Feature.AdminHubSignalR);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    if (activeAccountWebClientConfig.activeAccountId == null) {
      return;
    }

    if (hubRef.current) {
      return;
    }

    const hub = new POSSignalRHub(
      '/hubs/admin',
      activeAccountWebClientConfig,
      hubLogger
    );

    hub.on('forceLogout', (payload: { reason?: string }) => {
      console.warn(
        `[AdminHub] Server-initiated force logout. Reason: ${payload?.reason ?? 'unknown'}`
      );
      window.location.assign('/logout');
    });

    hub
      .start()
      .catch((err: Error) =>
        console.error(
          `[AdminHub] Failed to start: ${err?.message ?? String(err)}`
        )
      );

    hubRef.current = hub;

    return () => {
      hub.stop();
      hubRef.current = undefined;
    };
  }, [isEnabled, activeAccountWebClientConfig]);

  return <>{children}</>;
}
